import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import { UseCompanyWorkspaces, UseSelectedWorkspace } from "../../hooks/company";
import useScopedMetrics from "../../hooks/useScopedMetrics";
import { resolveCardWindow } from "../../helpers/cardWindow";
import "./cardScope.css";

/**
 * The dropdown a scoped card wears in its header, beside the gear.
 *
 * The admin decides in the drawer WHICH workspaces a card may show and whether
 * the dropdown lists workspaces or players. The viewer then switches with it.
 *
 * Nothing is drawn in the ordinary case — one workspace, showing its average.
 * That IS the app's default and an admin already knows it, so a caption saying
 * "this month · average per player" was noise on every card. The control only
 * appears when there is genuinely a choice to make.
 *
 * With a single workspace there is nothing to switch between, so no dropdown is
 * drawn — but silence confused everyone, so while that card is being edited a
 * dashed ghost says what would make it appear.
 */

const Chev = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 9l6 6 6-6" />
  </svg>
);

/**
 * Resolve a card's settings into the workspaces it should actually read.
 *
 * The viewer's choice in the card's dropdown lives HERE, not in the card. It
 * used to sit in the card's own state and was merged into the label only — so
 * picking a workspace renamed the dropdown while the numbers underneath went on
 * reading whatever the saved settings said. The pick has to reach the hook, or
 * it cannot change what is read.
 */
export const useCardScope = (settings = {}, ownWorkspaceId = null, boardWs = null, boardTime = null, inheritWorkspaceIds = []) => {
  const selected = UseSelectedWorkspace();
  const inheritKey = (inheritWorkspaceIds || []).filter(Boolean).map(String).join("|");
  const inScope = useMemo(() => {
    const inherited = (inheritWorkspaceIds || []).filter(Boolean).map(String);
    const picked = (settings.workspaceIds || []).filter(Boolean).map(String);
    const own = ownWorkspaceId || selected?._id;
    const ownStr = own ? String(own) : null;
    /* Rollup board (Head of Sales): inherit ALWAYS wins unless the card has an
       explicit multi-workspace pick that is not just the empty host. A single
       host id, or host+noise, used to beat inherit and leave Yeti on Team*
       tiles at 0 while the leaderboard (its own across ids) kept working. */
    if (inherited.length >= 2) {
      const nonHost = ownStr ? picked.filter((id) => id !== ownStr) : picked;
      if (nonHost.length >= 2) return nonHost;
      return inherited;
    }
    if (picked.length) return picked;
    return ownStr ? [ownStr] : [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.workspaceIds, ownWorkspaceId, selected?._id, inheritKey]);

  const saved = settings.cardPick || "all";
  const [localPick, setLocalPick] = useState(null);
  /* An admin editing the card wins over a viewer's earlier click. */
  useEffect(() => { setLocalPick(null); }, [saved]);
  /* The board-wide navbar filter, when the board is cross-workspace, is the
     single workspace control — it wins over this card's own pick. The card's
     own dropdown is hidden in that case (see the components that read
     `navControlled`), so there is one workspace control, not two competing. */
  const navControlled = boardWs != null;
  let pick = saved;
  if (navControlled) pick = boardWs;
  else if (localPick !== null) pick = localPick;
  const byPlayer = settings.filterBy === "player";
  /* A workspace pick narrows the data; a player pick does not — the card still
     reads every workspace in scope and filters to that person afterwards. */
  const reading = !byPlayer && pick !== "all" && inScope.includes(pick) ? [pick] : inScope;

  /* Time range: ScopeWindow pin (`window`) or PeriodField pin (`period`) wins;
     "Follow / Match the dashboard" tracks the navbar via boardTime. */
  const { window: win, custom } = resolveCardWindow(settings, {
    window: boardTime?.window,
    custom: boardTime?.custom,
  });
  const data = useScopedMetrics(reading, {
    window: win,
    custom,
    show: settings.avgMode === "total" ? "total" : "avg",
    plan: settings.metricPlan || null,
  });

  return { ...data, inScope, reading, pick, byPlayer, navControlled, choose: setLocalPick, window: win, custom };
};

const CardScope = ({ scope, onPick, editing, options, label: labelProp }) => {
  const workspaces = UseCompanyWorkspaces(true);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const wsName = useMemo(() => {
    const map = {};
    (workspaces || []).forEach((w) => { map[String(w._id)] = w.name; });
    return map;
  }, [workspaces]);

  const many = scope.inScope.length > 1;

  let items = [];
  let label = "";
  if (scope.byPlayer) {
    items = [{ v: "all", label: "All players" }, { sep: true }]
      .concat(scope.people.map((p) => ({ v: p.userId, label: p.name })));
    label = scope.pick === "all"
      ? "All players"
      : (scope.people.find((p) => p.userId === scope.pick) || {}).name || "All players";
  } else if (many) {
    items = [{ v: "all", label: "All workspaces" }, { sep: true }]
      .concat(scope.inScope.map((id) => ({ v: id, label: wsName[id] || "Workspace" })));
    label = scope.pick === "all" ? "All workspaces" : wsName[scope.pick] || "All workspaces";
  }

  /* A caller may supply its own list — the bonus cards drive a date-range
     picker through the same control rather than a second dropdown component. */
  if (options && options.length) {
    items = options;
    label = labelProp || label;
  }

  const hasDropdown = items.length > 0;

  /* One workspace has nothing to switch between, so no dropdown is drawn — but
     while this card is being edited a dashed ghost says what unlocks it. */
  let control = null;
  if (hasDropdown) control = "dropdown";
  else if (editing) control = "ghost";

  return (
    <div className="cs">
      {control === "dropdown" && (
        <div className={`cs-dd${open ? " open" : ""}`} ref={ref}>
          <button type="button" className="cs-b" onClick={() => setOpen((v) => !v)}>
            <span className="cs-v">{label}</span>
            <Chev />
          </button>
          {open && (
            <div className="cs-m" role="listbox">
              {items.map((it) => (it.sep
                ? <span className="cs-sep" key="sep" />
                : (
                  <button
                    key={it.v}
                    type="button"
                    role="option"
                    aria-selected={scope.pick === it.v}
                    className={`cs-i${scope.pick === it.v ? " on" : ""}`}
                    onClick={() => { onPick(it.v); setOpen(false); }}
                  >
                    <span>{it.label}</span>
                    <svg className="cs-ck" viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                  </button>
                )))}
            </div>
          )}
        </div>
      )}
      {control === "ghost" && (
        /* Compact on purpose: it shares the header row with the card's own
           title, and the full explanation is one tap away on the ⓘ beside
           Workspaces in the panel. A sentence here collided with the title. */
        <span className="cs-ghost" title="Tick a second workspace and this becomes a dropdown">
          <Chev />
          Needs 2+ workspaces
        </span>
      )}
    </div>
  );
};

CardScope.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  scope: PropTypes.object.isRequired,
  onPick: PropTypes.func.isRequired,
  editing: PropTypes.bool,
  /** Explicit menu items, overriding the workspace/player list. */
  options: PropTypes.arrayOf(PropTypes.shape({ v: PropTypes.string, label: PropTypes.string })),
  label: PropTypes.string,
};
CardScope.defaultProps = { editing: false, options: null, label: null };

export default CardScope;
